import * as React from 'react';
import { Icon } from '@iconify/react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
  Badge,
  Box,
  Grid,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import constant from '../../../../../utils/constants';
import { useMyProfileHooks } from './hooks/myProfilesHooks';
import { useAppSelector } from '../../../../../app/redux/hooks';
import CustomButton from '../../../../components/buttons/CustomButton';
import ThemeSetting from '../themeSetting/ThemeSetting';
import UserNotification from '../notification/UserNotification';
import ChangePassword from '../changePassword/ChangePassword';
import DefaultPage from '../DefaultPage';
import UserProfile from './UserProfile';
import { addLogout } from '../../../../redux/reducer/commonSlices/loginSlice';
import './MyProfile.scss';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

interface Props {
  isOpen?: string;
  handleClose?: () => void;
}

function MyProfile({ isOpen, handleClose }: Props) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.up('sm'));

  const urls = {
    logout: addLogout
  };

  const { isLoading } = useAppSelector(state => state.logout);
  const { data } = useAppSelector(state => state.auth);
  const themes = useAppSelector(state => state.theme.theme);

  const { handleBadgeClick, handleSignout, selectedBadge } = useMyProfileHooks(urls);

  const roletype = data?.role;

  const yaanTRAC = roletype === 'ROLE_YAANTRAC_ADMIN';
  const APuser =
    roletype === 'ROLE_AUTOPLANNER_ADMIN' ||
    roletype === 'ROLE_OPERATOR' ||
    roletype === 'ROLE_AGENT' ||
    roletype === 'ROLE_SUB_AGENT';

  return (
    <Dialog
      open={isOpen === 'myprofile'}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      className={`profileDialog ${themes}`}
      TransitionComponent={Transition}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Stack direction='row' justifyContent='space-between' p={2}>
          <Typography paragraph className='setting' mb={3}>
            {constant.Settings}
          </Typography>
          <Box className='closeicon' onClick={handleClose}>
            <CloseIcon className='customicon' />
          </Box>
        </Stack>
        {isXs ? (
          <Grid container mt={1} px={2}>
            <Grid item md={4} lg={3} sm={6}>
              <Box className='rootProfile'>
                <Box className='navbarProfile'>
                  <Stack className='componentsIcons' direction='column'>
                    <Badge
                      className={selectedBadge === 1 ? 'active' : 'nonActive'}
                      onClick={() => handleBadgeClick(1)}
                    >
                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Icon
                          className={selectedBadge === 1 ? 'activeIcon icon' : 'icon'}
                          height='25'
                          width='25'
                          icon='iconamoon:profile'
                        />
                        <Typography className='options'>{constant.Profile}</Typography>
                      </Stack>
                    </Badge>
                    {!APuser && roletype && (
                      <Badge
                        className={selectedBadge === 2 ? 'active' : 'nonActive'}
                        onClick={() => handleBadgeClick(2)}
                      >
                        <Stack direction='row' alignItems='center' spacing={2}>
                          <Icon
                            className={selectedBadge === 2 ? 'activeIcon icon' : 'icon'}
                            icon='tdesign:notification'
                          />
                          <Typography className='options'>
                            {constant.Notifications}
                          </Typography>
                        </Stack>
                      </Badge>
                    )}
                    {yaanTRAC && (
                      <Badge
                        className={selectedBadge === 5 ? 'active' : 'nonActive'}
                        onClick={() => handleBadgeClick(5)}
                      >
                        <Stack direction='row' alignItems='center' spacing={2}>
                          <Icon
                            className={selectedBadge === 5 ? 'activeIcon icon' : 'icon'}
                            icon='fluent:page-fit-16-regular'
                          />
                          <Typography className='options'>
                            {constant.DefaultView}
                          </Typography>
                        </Stack>
                      </Badge>
                    )}

                    <Badge
                      className={selectedBadge === 3 ? 'active' : 'nonActive'}
                      onClick={() => handleBadgeClick(3)}
                    >
                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Icon
                          className={selectedBadge === 3 ? 'activeIcon icon' : 'icon'}
                          icon='solar:lock-password-linear'
                        />
                        <Typography className='options'>
                          {constant.ChangePassword}
                        </Typography>
                      </Stack>
                    </Badge>
                  </Stack>
                </Box>
                <CustomButton
                  category='Log Out'
                  onClick={handleSignout}
                  loading={isLoading}
                  className='log-out-button'
                  sx={{ width: '90%' }}
                />
              </Box>
            </Grid>
            <Grid item md={8} lg={9} sm={6}>
              {selectedBadge === 1 && <UserProfile />}
              {selectedBadge === 2 && <UserNotification />}
              {selectedBadge === 3 && <ChangePassword handleClose={handleClose} />}
              {selectedBadge === 4 && <ThemeSetting />}
              {selectedBadge === 5 && <DefaultPage />}
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Grid item md={4} lg={3} sm={6}>
              <Box className='rootProfileXS'>
                <Box className='navbarProfileXS'>
                  <Stack className='componentsIconsXS' direction='column'>
                    <Badge
                      className={selectedBadge === 1 ? 'activeXS' : 'nonActive'}
                      onClick={() => handleBadgeClick(1)}
                    >
                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Icon
                          className={
                            selectedBadge === 1 ? 'activeIconXS iconXS' : 'iconXS'
                          }
                          height='25'
                          width='25'
                          icon='iconamoon:profile'
                        />
                      </Stack>
                    </Badge>

                    {!(
                      data?.role === 'ROLE_SUB_AGENT' ||
                      data?.role === 'ROLE_AGENT' ||
                      data?.role === 'ROLE_OPERATOR' ||
                      data?.role === 'ROLE_AUTOPLANNER_ADMIN'
                    ) && (
                      <>
                        <Badge
                          className={selectedBadge === 2 ? 'activeXS' : 'nonActive'}
                          onClick={() => handleBadgeClick(2)}
                        >
                          <Stack direction='row' alignItems='center' spacing={2}>
                            <Icon
                              className={
                                selectedBadge === 2 ? 'activeIconXS iconXS' : 'iconXS'
                              }
                              icon='tdesign:notification'
                            />
                          </Stack>
                        </Badge>
                        <Badge
                          className={selectedBadge === 5 ? 'activeXS' : 'nonActive'}
                          onClick={() => handleBadgeClick(5)}
                        >
                          <Stack direction='row' alignItems='center' spacing={2}>
                            <Icon
                              className={
                                selectedBadge === 5 ? 'activeIconXS iconXS' : 'iconXS'
                              }
                              icon='fluent:page-fit-16-regular'
                            />
                          </Stack>
                        </Badge>
                      </>
                    )}

                    <Badge
                      className={selectedBadge === 3 ? 'activeXS' : 'nonActive'}
                      onClick={() => handleBadgeClick(3)}
                    >
                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Icon
                          className={
                            selectedBadge === 3 ? 'activeIconXS iconXS' : 'iconXS'
                          }
                          icon='solar:lock-password-linear'
                        />
                      </Stack>
                    </Badge>
                  </Stack>
                </Box>
                <Icon
                  icon='material-symbols:logout'
                  width='28'
                  height='28'
                  onClick={handleSignout}
                  className='log-out-buttonXS'
                  style={{ color: '#292d32' }}
                />
              </Box>
            </Grid>
            <Grid item md={8} lg={9} sm={6}>
              {selectedBadge === 1 && <UserProfile />}
              {selectedBadge === 2 && <UserNotification />}
              {selectedBadge === 3 && <ChangePassword handleClose={handleClose} />}
              {selectedBadge === 4 && <ThemeSetting />}
              {selectedBadge === 5 && <DefaultPage />}
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MyProfile;
